import React from 'react';
import { AlertCircle, Trash2, Loader2 } from 'lucide-react';

/**
 * Composant modal de confirmation de suppression réutilisable
 * Identique à celui utilisé dans ObjectifList.js
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Si le modal est ouvert
 * @param {Function} props.onClose - Fonction pour fermer le modal
 * @param {Function} props.onConfirm - Fonction pour confirmer la suppression
 * @param {string} props.itemName - Nom de l'élément à supprimer
 * @param {boolean} props.isDeleting - Si la suppression est en cours
 * @param {string} props.itemType - Type d'élément (par défaut: "source")
 */
const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  itemName, 
  isDeleting,
  itemType = "source"
}) => {
  if (!isOpen) return null;

  // Déterminer le texte en fonction du type d'élément
  const getItemTypeText = () => {
    switch(itemType) {
      case 'objective':
        return { title: "Supprimer l'objectif", itemText: "objectif" };
      case 'source':
      default:
        return { title: "Supprimer la source", itemText: "source" };
    }
  };

  const { title, itemText } = getItemTypeText();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">Cette action est irréversible</p>
          </div>
        </div>
        
        <p className="text-gray-700 mb-6">
          Êtes-vous sûr de vouloir supprimer la {itemText} "<strong className="font-bold text-gray-900">{itemName}</strong>" ? 
          Toutes les données associées seront définitivement perdues.
        </p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Suppression...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Supprimer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
